import { useState } from "react";
import { mutate, useSWRConfig } from "swr";
import { productService } from "../../api_client";
import type { UpdateProductDto } from "../../../api";
import { showErrorToast, showSuccessToast } from "../../../utils/toasts";

export type ProductLifecycleAction = "inactive" | "discontinued" | "reactivate";

function extractErrorMessage(error: unknown, fallback: string): string {
  const typed = error as {
    response?: { data?: { message?: string | string[] } };
    message?: string;
  };
  const raw = typed?.response?.data?.message ?? typed?.message;
  if (Array.isArray(raw)) return raw.join(", ");
  return raw ?? fallback;
}

export function useProductLifecycleActions() {
  const { mutate: swrMutate } = useSWRConfig();
  const [isSaving, setIsSaving] = useState(false);

  async function updateLifecycle(productId: number, action: ProductLifecycleAction) {
    setIsSaving(true);
    try {
      const payload: UpdateProductDto =
        action === "reactivate"
          ? { active: true }
          : { active: false, lifecycleStatus: action };

      await productService.updateProduct(productId, payload);

      await Promise.all([
        mutate(`product-${productId}`),
        swrMutate((key) => typeof key === "string" && key.startsWith("my-products")),
      ]);

      showSuccessToast(
        action === "reactivate"
          ? "Product reactivated."
          : action === "discontinued"
            ? "Product discontinued."
            : "Product set inactive.",
      );
    } catch (error) {
      showErrorToast(
        extractErrorMessage(error, "Failed to update product status."),
      );
      throw error;
    } finally {
      setIsSaving(false);
    }
  }

  return {
    updateLifecycle,
    isSaving,
  };
}
